/**
 * Popover component for the Indigo design system.
 *
 * A floating card anchored to a trigger, for content richer than a tooltip:
 * a preview of a linked record, a short thread, an action or two. Unlike
 * `Tooltip` it is interactive — the pointer can move into it and its links
 * and buttons can be used.
 *
 * Opens on hover/focus by default (a hover card); pass `trigger="click"` for
 * a popover that stays open until dismissed. It renders into `document.body`,
 * so it is not clipped by a scrolling container or a modal body.
 *
 * @example
 * ```tsx
 * <Popover content={<RecordPreview record={record} />}>
 *   <a href={record.url}>{record.title}</a>
 * </Popover>
 * ```
 */
import Tippy from "@tippyjs/react";
import { clsx } from "clsx";
import type { ReactElement, ReactNode } from "react";
import { zIndex } from "../../styles/tokens";
import type { Placement } from "../Tooltip";
import "./Popover.css";

export type PopoverTrigger = "hover" | "click";

export interface PopoverProps {
  /** Content of the floating card. */
  content: ReactNode;

  /**
   * Trigger element. Must accept a ref (a DOM element, or a component that
   * forwards its ref).
   */
  children: ReactElement;

  /**
   * `hover` opens on pointer hover and keyboard focus; `click` toggles on
   * click and closes on an outside click or Escape.
   * @default "hover"
   */
  trigger?: PopoverTrigger;

  /** Placement relative to the trigger. @default "bottom-start" */
  placement?: Placement;

  /** Maximum width of the card in pixels. @default 320 */
  maxWidth?: number;

  /** Controlled visibility. Leave unset to let `trigger` drive it. */
  open?: boolean;

  /** Called when the popover is dismissed (outside click, Escape). */
  onClose?: () => void;

  /** Additional CSS classes applied to the card. */
  className?: string;

  /** Test id applied to the card. */
  "data-testid"?: string;
}

const TRIGGER_EVENTS: Record<PopoverTrigger, string> = {
  hover: "mouseenter focus",
  click: "click",
};

/** Hover cards open after a short pause so a pointer passing by doesn't flash
 *  them, and linger long enough for the pointer to travel into the card. */
const HOVER_DELAY: [number, number] = [200, 150];

export const Popover = ({
  content,
  children,
  trigger = "hover",
  placement = "bottom-start",
  maxWidth = 320,
  open,
  onClose,
  className,
  "data-testid": dataTestId,
}: PopoverProps) => {
  const isControlled = open !== undefined;

  return (
    <Tippy
      content={
        <div
          className={clsx("indigo-popover", className)}
          data-testid={dataTestId}
        >
          {content}
        </div>
      }
      interactive
      // Lets the pointer cross the gap between trigger and card.
      interactiveBorder={8}
      placement={placement}
      offset={[0, 8]}
      arrow={false}
      maxWidth={maxWidth}
      zIndex={zIndex.antPopover}
      appendTo={() => document.body}
      delay={trigger === "hover" ? HOVER_DELAY : 0}
      // The card styles its own box; Tippy's themed box would paint a dark
      // tooltip background around it.
      theme="indigo-popover"
      {...(isControlled
        ? { visible: open, onClickOutside: () => onClose?.() }
        : { trigger: TRIGGER_EVENTS[trigger], onHidden: () => onClose?.() })}
    >
      {children}
    </Tippy>
  );
};

Popover.displayName = "Popover";
